import React from 'react';
import PropTypes from 'prop-types';

import { Button } from 'react-bootstrap';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

import SwalDialog from 'lib/utils/toast';
import { httpGet } from 'lib/networking/http';
import {
  REBATE_ANALYSIS_UPLOAD_FILE,
  REBATE_ANALYSIS_UPLOAD_PRESIGNED_URL,
  getURL,
} from 'lib/networking/endpoints';

const STATUSES = {
  LOADING: 'loading',
  RUNNING: 'running',
  COMPLETED: 'completed',
  READY: 'ready',
  FAILED: 'failed',
  FAILED_LOAD: 'failed: load',
  FAILED_TIMEOUT: 'failed: timeout', // legacy — keep for existing DB records
  FAILED_TIMEOUT_LOAD: 'failed: timeout: load', // load phase stalled — not retryable
};

function RebateAnalysisUploadRow({ upload, onDelete, onRunAnalysis, isRunning }) {
  const history = useHistory();
  const status = upload.status || '';
  const isLoading = status === STATUSES.LOADING;
  const isRunningAnalysis = status.startsWith(STATUSES.RUNNING) || isRunning;
  const isProcessing = isLoading || isRunningAnalysis;
  const isCompleted = status === STATUSES.COMPLETED;
  const canRunAnalysis =
    status === STATUSES.READY ||
    (status.startsWith(STATUSES.FAILED) &&
      status !== STATUSES.FAILED_LOAD &&
      status !== STATUSES.FAILED_TIMEOUT &&
      status !== STATUSES.FAILED_TIMEOUT_LOAD);

  let runButtonLabel = 'Generate Rebate Report';
  if (isLoading) runButtonLabel = 'Loading to Snowflake…';
  else if (isRunningAnalysis) runButtonLabel = 'Generating Report…';
  else if (isCompleted) runButtonLabel = 'Report Generated';

  const downloadFromPresignedUrl = async url => {
    const response = await httpGet(url);
    const presignedUrl = response.data?.data;
    if (!presignedUrl) throw new Error('No download URL returned.');
    const link = document.createElement('a');
    link.href = presignedUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadFile = async fileType => {
    try {
      const url = getURL(REBATE_ANALYSIS_UPLOAD_FILE, {
        upload_id: upload.id,
        file_type: fileType,
      });
      await downloadFromPresignedUrl(url);
    } catch (err) {
      SwalDialog(
        'error',
        `Download failed: ${err.response?.data?.message || err.message}`,
        'Error',
      );
    }
  };

  // eslint-disable-next-line prettier/prettier
  const handleDownloadOriginal = async() => {
    try {
      const url = getURL(REBATE_ANALYSIS_UPLOAD_PRESIGNED_URL, { upload_id: upload.id });
      await downloadFromPresignedUrl(url);
    } catch (err) {
      SwalDialog(
        'error',
        `Download failed: ${err.response?.data?.message || err.message}`,
        'Error',
      );
    }
  };

  return (
    <tr>
      <td className="py-3">{upload.id}</td>
      <td className="py-3">{upload.file_name}</td>
      <td className="py-3">
        <Button
          variant="outline-primary"
          size="sm"
          onClick={onRunAnalysis}
          disabled={isProcessing || !canRunAnalysis}
        >
          {runButtonLabel}
        </Button>
      </td>
      <td className="py-3">
        <Button
          variant="outline-secondary"
          size="sm"
          className="mr-1 mb-1"
          onClick={handleDownloadOriginal}
        >
          Original File
        </Button>
        {isCompleted && (
          <Button
            variant="outline-success"
            size="sm"
            className="mr-1 mb-1"
            onClick={() => handleDownloadFile('report')}
          >
            Report XLSX
          </Button>
        )}
        <Button
          variant="outline-info"
          size="sm"
          className="mr-1 mb-1"
          onClick={() => history.push(`/rebate-analysis/${upload.id}/view`)}
        >
          Preview
        </Button>
      </td>
      <td className="py-3">{upload.status}</td>
      <td className="py-3">
        {upload.date_created ? dayjs(upload.date_created).format('MM/DD/YY HH:mm') : '-'}
      </td>
      <td className="py-3">{upload.uploaded_by || '-'}</td>
      <td className="py-3">
        <Button variant="outline-danger" size="sm" onClick={onDelete} disabled={isProcessing}>
          Remove
        </Button>
      </td>
    </tr>
  );
}

RebateAnalysisUploadRow.propTypes = {
  isRunning: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onRunAnalysis: PropTypes.func.isRequired,
  upload: PropTypes.object.isRequired,
};

RebateAnalysisUploadRow.defaultProps = {
  isRunning: false,
};

export default RebateAnalysisUploadRow;
